/**
 * Cloud VMs on the Machines page (ADR 0106 amendment 24 Oct 2026): one calm band above the cards,
 * for an owner or admin only.
 *
 *   Not connected   what connecting AWS gives (create, stop, start, restart, delete, stop when
 *                   idle) and Connect AWS, which opens the two steps in place.
 *   Connected       when instances in AWS run this workspace's agent but Orgus does not control
 *                   them yet (made by hand): how many, Review VMs, then one Adopt action. Adopted
 *                   VMs get the VM items on their card's menu like the VMs Orgus created.
 *
 * Hidden for everyone else, and when the server cannot reach AWS at all.
 */
import { Cloud, CloudCog, Server } from '@orgus/ui/icons';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { CloudAdoptResult, CloudDiscovery, CloudInstanceCandidate } from '@orgus/protocol';
import { Button, ErrorState, RowStatus, Tooltip, cn, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { CARD_SURFACE } from '../machines/MachineCard.tsx';
import { ConnectAws } from './CreateWithAws.tsx';
import { adoptVms, discoverAws, useCloud } from './data.ts';
import type { CloudView } from './data.ts';
import { adoptTitle, candidateFacts, cloudBandMode } from './model.ts';

export function MachinesCloudBand() {
  const cloud = useCloud();
  return <MachinesCloudBandView cloud={cloud} />;
}

export function MachinesCloudBandView({ cloud, discovery: seeded }: { cloud: CloudView; discovery?: CloudDiscovery | null }) {
  const mode = cloudBandMode(cloud);
  if (mode === 'connect') return <ConnectBand cloud={cloud} />;
  if (mode === 'adopt') return <AdoptBand seeded={seeded} connectedKey={cloud.aws?.roleArn ?? ''} />;
  return null;
}

function Band({ icon, title, caption, action, children, testId }: { icon: ReactNode; title: string; caption: string; action?: ReactNode; children?: ReactNode; testId: string }) {
  return (
    <section aria-label={title} className={cn(CARD_SURFACE, 'flex flex-col gap-4 p-4')} data-testid={testId}>
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-raised text-fg-subtle" aria-hidden>
          {icon}
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="text-ui font-medium text-fg">{title}</p>
          <p className="text-caption text-fg-subtle">{caption}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function ConnectBand({ cloud }: { cloud: CloudView }) {
  const [open, setOpen] = useState(cloud.aws?.state === 'pending');
  return (
    <Band
      testId="cloud-band-connect"
      icon={<Cloud className="size-4" />}
      title="Control your cloud VMs from Orgus"
      caption="Create, start, stop, restart and delete VMs here, and stop them when idle."
      action={
        !open && (
          <Button variant="secondary" size="sm" icon={<Cloud className="text-accent-text" />} onClick={() => setOpen(true)} data-testid="cloud-band-open">
            Connect AWS
          </Button>
        )
      }
    >
      {open && (
        <div className="border-t border-border-subtle pt-4">
          <ConnectAws aws={cloud.aws ?? null} startOpen />
        </div>
      )}
    </Band>
  );
}

type Load = { phase: 'loading' } | { phase: 'error'; error: UiError } | { phase: 'ready'; d: CloudDiscovery };

function AdoptBand({ seeded, connectedKey }: { seeded?: CloudDiscovery | null; connectedKey: string }) {
  const [load, setLoad] = useState<Load>(seeded ? { phase: 'ready', d: seeded } : { phase: 'loading' });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<CloudAdoptResult | null>(null);
  const [error, setError] = useState<UiError | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoad({ phase: 'ready', d: await discoverAws() });
    } catch (e) {
      setLoad({ phase: 'error', error: toUiError(e, { what: 'Orgus could not list the instances in AWS.' }) });
    }
  }, []);
  useEffect(() => {
    if (!seeded) void refresh();
  }, [seeded, refresh, connectedKey]);

  // Loading and an empty list stay quiet: the band appears only when there is something to adopt.
  if (load.phase === 'loading') return null;
  if (load.phase === 'error') {
    return (
      <div className={cn(CARD_SURFACE, 'px-4 py-3')} data-testid="cloud-band-error">
        <ErrorState variant="inline" error={load.error} onRetry={() => void refresh()} />
      </div>
    );
  }
  const list = load.d.candidates;
  if (!list.length && !result) return null;
  const ready = list.filter((c) => c.adoptable);

  const adopt = async (ids: string[], key: string) => {
    setBusy(key);
    setError(null);
    try {
      setResult(await adoptVms(ids));
      await refresh();
    } catch (e) {
      setError(toUiError(e, { what: 'The instances were not adopted.' }));
    } finally {
      setBusy(null);
    }
  };

  if (!list.length && result) {
    return (
      <div className={cn(CARD_SURFACE, 'flex items-center gap-3 px-4 py-3')} role="status" aria-live="polite" data-testid="cloud-band-adopted">
        <RowStatus tone="success" icon="done" label={`Adopted ${result.adopted.length} ${result.adopted.length === 1 ? 'VM' : 'VMs'}: each card's menu now starts, stops, restarts and deletes it`} />
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setResult(null)}>
          Dismiss
        </Button>
      </div>
    );
  }

  return (
    <Band
      testId="cloud-band-adopt"
      icon={<CloudCog className="size-4" />}
      title={adoptTitle(list.length)}
      caption={`Adopt them to control them here, in ${load.d.region}.`}
      action={
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="cloud-band-review">
            {open ? 'Hide VMs' : 'Review VMs'}
          </Button>
          {ready.length > 0 && (
            <Button variant="secondary" size="sm" icon={<CloudCog className="text-accent-text" />} loading={busy === 'all'} disabled={!!busy} onClick={() => void adopt(ready.map((c) => c.instanceId), 'all')} data-testid="cloud-band-adopt-all">
              {ready.length === 1 ? 'Adopt VM' : `Adopt ${ready.length} VMs`}
            </Button>
          )}
        </div>
      }
    >
      {open && (
        <ul className="flex flex-col border-t border-border-subtle pt-2" aria-label="Instances to adopt" data-testid="cloud-band-list">
          {list.map((c) => (
            <CandidateRow key={c.instanceId} c={c} busy={busy === c.instanceId} disabled={!!busy} onAdopt={() => void adopt([c.instanceId], c.instanceId)} />
          ))}
        </ul>
      )}
      {result && result.adopted.length > 0 && (
        <div role="status" aria-live="polite" data-testid="cloud-band-adopted">
          <RowStatus tone="success" icon="done" label={`Adopted ${result.adopted.map((v) => v.name).join(', ')}: each card's menu now starts, stops, restarts and deletes it`} />
        </div>
      )}
      {result && result.failed.length > 0 && (
        <ErrorState
          variant="inline"
          error={{ what: `${result.failed.length} ${result.failed.length === 1 ? 'instance was' : 'instances were'} not adopted.`, why: result.failed.map((f) => `${f.instanceId}: ${f.error}`).join(' '), next: 'Fix what each says, then adopt again.' }}
        />
      )}
      {error && <ErrorState variant="inline" error={error} />}
    </Band>
  );
}

function CandidateRow({ c, busy, disabled, onAdopt }: { c: CloudInstanceCandidate; busy: boolean; disabled: boolean; onAdopt: () => void }) {
  const status = c.state === 'running' || c.state === 'pending' ? { tone: 'success' as const, icon: 'done' as const, label: 'Running' } : { tone: 'neutral' as const, icon: 'stopped' as const, label: 'Stopped' };
  return (
    <li className="flex min-h-10 min-w-0 items-center gap-3 py-1" data-testid="cloud-candidate">
      <Server className="size-4 shrink-0 text-fg-subtle" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="min-w-0 truncate text-ui text-fg">{c.name ?? c.instanceId}</p>
        <p className="min-w-0 truncate text-caption text-fg-subtle">
          <span className="font-mono">{c.instanceId}</span>, {candidateFacts(c)}
        </p>
      </div>
      <RowStatus {...status} className="shrink-0" hideLabel="below-lg" />
      {c.adoptable ? (
        <Button size="sm" variant="ghost" loading={busy} disabled={disabled} onClick={onAdopt}>
          Adopt
        </Button>
      ) : (
        <Tooltip content={c.blocked ?? 'It cannot be adopted.'}>
          <span className="shrink-0 text-caption text-fg-subtle" tabIndex={0}>
            Tag orgus=fleet first
          </span>
        </Tooltip>
      )}
    </li>
  );
}
